/**
 * S5 — klickbare LEX/JUD-Suche ohne Composer.
 * ARKO bleibt in der Sidebar; Ergebnisse zum Selbst-Bauen.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, Copy, ExternalLink, Loader2, Pin, Route, Scale, Search, X } from 'lucide-react';
import { portalAuthzHeaders } from '../../../constants';
import { portalSucheSurfaceClass } from '../portalAppearance';
import { usePortalAppearance } from '../portalAppearanceContext';
import { consumeArkoSse } from '../arkoStreamClient';
import {
  ArkoResponseProof,
  ArkoTrustBadge,
  LexVerifiedQuoteBlock,
} from '../ArkoBotSources';
import {
  parseJudicatureProof,
  stripJudicatureStampForDisplay,
  type JudicatureProof,
  type JudicatureProofCase,
} from '../judicatureProof';
import { formatBotMessageForDisplay, parseLexProof, type LexProof } from '../lexProof';
import {
  PORTAL_JUD_SEARCH_FIELD_MARKER,
  PORTAL_JUD_SEARCH_LAWYER_MARKER,
} from '../portalLexJudJoin';
import { parseParagraphQuery, suggestParagraphRefs } from '../portalParagraphRef';
import { quelleGueltigAbLine } from '../portalParagraphAnschluss';
import { bausteinRowIsGreen, resolveBausteinUnlock, type BausteinSeatInput } from '../portalBausteinUnlock';
import { judicatureChipVisible } from '../portalBausteinSitz';
import { PORTAL_SHELL } from '../portalTheme';
import {
  PORTAL_SUCHE_BAR_PIN_HINT,
  PORTAL_SUCHE_BAR_PIN_LABEL,
  PORTAL_SUCHE_CLASS,
  PORTAL_SUCHE_COPY_BLOCKED,
  PORTAL_SUCHE_COPY_LABEL,
  PORTAL_SUCHE_EMPTY_EXPLORE,
  PORTAL_SUCHE_ERROR_ABORTED,
  PORTAL_SUCHE_ERROR_EMPTY_JUD,
  PORTAL_SUCHE_ERROR_EMPTY_LEX,
  PORTAL_SUCHE_ERROR_TIMEOUT,
  PORTAL_SUCHE_ERROR_UNREACHABLE,
  PORTAL_SUCHE_HEADING_COMPOSE,
  PORTAL_SUCHE_HEADING_JUD_CITE,
  PORTAL_SUCHE_HEADING_JUD_EXPLORE,
  PORTAL_SUCHE_HEADING_LEX_CITE,
  PORTAL_SUCHE_HEADING_LEX_EXPLORE,
  PORTAL_SUCHE_HEADING_RIS_BLOCKS,
  PORTAL_SUCHE_INPUT_LABEL_JUD,
  PORTAL_SUCHE_INPUT_LABEL_LEX,
  PORTAL_SUCHE_MARKED_BAR_HINT,
  PORTAL_SUCHE_MARKED_BAR_SELECTED,
  PORTAL_SUCHE_MARKED_CLASS,
  PORTAL_SUCHE_MARKED_EMPTY_HINT,
  PORTAL_SUCHE_MARKED_ORIENTATION_LEAD,
  PORTAL_SUCHE_MARKED_PATH_HIT,
  PORTAL_SUCHE_MARKED_PATH_KIND,
  PORTAL_SUCHE_MARKED_PATH_LABEL,
  PORTAL_SUCHE_MARKED_PATH_RETRIEVE,
  PORTAL_SUCHE_MARKED_PATH_SKIP_STATUS,
  PORTAL_SUCHE_MARK_PIN_LABEL,
  PORTAL_SUCHE_MARKED_PIN_LABEL,
  PORTAL_SUCHE_MODE_HINT_JUD_CITE,
  PORTAL_SUCHE_MODE_HINT_JUD_EXPLORE,
  PORTAL_SUCHE_MODE_HINT_LEX_CITE,
  PORTAL_SUCHE_MODE_HINT_LEX_EXPLORE,
  PORTAL_SUCHE_NORM_REFS_LABEL,
  PORTAL_SUCHE_PAGE_SUBTITLE,
  PORTAL_SUCHE_PAGE_TITLE,
  PORTAL_SUCHE_PENDING,
  PORTAL_SUCHE_PLACEHOLDER_JUD,
  PORTAL_SUCHE_PLACEHOLDER_LEX,
  PORTAL_SUCHE_PROOF_LABEL,
  PORTAL_SUCHE_PROOF_LEX_SECTION,
  PORTAL_SUCHE_RECOGNIZED_PREFIX,
  PORTAL_SUCHE_RIS_LABEL,
  PORTAL_SUCHE_RIS_OPEN_LABEL,
  PORTAL_SUCHE_SUBMIT_LABEL,
  PORTAL_SUCHE_TABLIST_LABEL,
  PORTAL_SUCHE_TABS,
  portalSucheTabs,
  PORTAL_SUCHE_TIMEOUT_MS,
  PORTAL_SUCHE_TRUST_UNVERIFIED_TITLE,
  PORTAL_SUCHE_TRUST_VERIFIED_TITLE,
  applyKeywordNormProof,
  buildPortalSucheAnswerPath,
  buildPortalSucheComposePath,
  buildPortalSucheMarkedChipWebhookBodies,
  buildPortalSucheMarkedOrientation,
  buildPortalSucheWebhookBody,
  filterPortalSucheJudProofToMarkedChips,
  filterPortalSucheJudProofToMarkedKeys,
  filterPortalSucheLexProofToMarkedChips,
  filterPortalSucheLexProofToMarkedIds,
  clearPortalSuchePersist,
  loadPortalSuchePersist,
  mergePortalSucheLexProofs,
  newPortalSucheRequestId,
  parsePortalSucheHitRaw,
  portalSucheComposeBlocks,
  portalSucheBarComposeButtonLabel,
  portalSucheChipsToExploreHits,
  portalSucheComposeSourcesFromLexProof,
  portalSucheComposeButtonEnabled,
  portalSucheComposeButtonLabel,
  portalSucheComposeSnapshotFromHit,
  portalSucheEnterAction,
  portalSucheExploreHitsFromJudCases,
  portalSucheExploreHitsFromLexSources,
  portalSucheExternalRisHref,
  portalSucheCardSummary,
  portalSucheCrossRefChips,
  portalSucheHeadExplanation,
  portalSucheOrientationModelText,
  portalSucheRunSummaryText,
  portalSucheVisibleOrientation,
  portalSucheWortlautAbsaetze,
  portalSucheComposePathSteps,
  portalSucheHitFieldsFromPersist,
  portalSucheHttpErrorMessage,
  portalSucheKindFromTab,
  portalSucheMarkedHits,
  portalSucheMergeMarkedHits,
  isKeywordNormQuery,
  portalSucheMode,
  portalSuchePersistFromView,
  portalSuchePersistSeatId,
  portalSuchePinQueryChip,
  portalSucheRemoveQueryChip,
  portalSucheSearchHitFieldsFromPersist,
  portalSucheSearchSnapshotFromHit,
  portalSucheStreamPath,
  portalSucheToggleMarkedKeys,
  portalSuchePathChipCountLabel,
  savePortalSuchePersist,
  unwrapPortalArkoWebhookPayload,
  type PortalSucheComposeBlock,
  type PortalSucheComposePath,
  type PortalSucheExploreHit,
  type PortalSucheKind,
  type PortalSucheMode,
  type PortalSuchePersistedState,
  type PortalSucheQueryChip,
} from '../portalSuche';

export interface SucheViewProps {
  webhookUrl: string;
  extraBody?: Record<string, unknown>;
  /** Sitz für PORTAL_BAUSTEIN_FREISCHALTUNG. ARKO ohne Schalter bleibt aus. */
  seat?: BausteinSeatInput;
}

type PortalSucheHit = {
  kind: PortalSucheKind;
  mode: PortalSucheMode;
  query?: string;
  text: string;
  lexProof: LexProof | null;
  judProof: JudicatureProof | null;
  composeBlocks?: PortalSucheComposeBlock[];
  composePath?: PortalSucheComposePath;
};

function toSucheHit(
  kind: PortalSucheKind,
  mode: PortalSucheMode,
  payload: unknown,
  draftText = '',
  query = '',
): PortalSucheHit {
  const raw = parsePortalSucheHitRaw(kind, payload, draftText);
  const keyword = kind === 'lex' && mode === 'explore' && isKeywordNormQuery(query);
  const lexProof = keyword
    ? applyKeywordNormProof(query, parseLexProof(raw.lexProof))
    : parseLexProof(raw.lexProof);
  return {
    kind: raw.kind,
    mode,
    query,
    text: raw.text,
    lexProof,
    judProof: keyword ? null : parseJudicatureProof(raw.judProof),
  };
}

function sucheHitFromPersist(state: PortalSuchePersistedState): PortalSucheHit | null {
  const fields = portalSucheHitFieldsFromPersist(state);
  if (!fields) return null;
  return {
    kind: fields.kind,
    mode: fields.mode,
    text: fields.text,
    lexProof: null,
    judProof: null,
    composeBlocks: fields.composeBlocks,
    composePath: fields.composePath,
  };
}

function sucheSearchHitFromPersist(state: PortalSuchePersistedState): PortalSucheHit | null {
  const fields = portalSucheSearchHitFieldsFromPersist(state);
  if (!fields) return null;
  return {
    kind: fields.kind,
    mode: fields.mode,
    query: fields.query,
    text: fields.text,
    lexProof: fields.sources.length ? parseLexProof({ sources: fields.sources }) : null,
    judProof: fields.cases.length ? ({ cases: fields.cases } as JudicatureProof) : null,
  };
}

/** Schwarzer Grund, feine lila Kante — wie die Aktenliste. */
const SUCHE_GROUND = 'bg-[#07070a]';
const SUCHE_EDGE = 'rounded-lg border border-violet-400/30 bg-[#07070a]';
/** Knopf wie Neue Akte und Öffnen. */
const SUCHE_BTN =
  'portal-suche-standard inline-flex shrink-0 items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-45';
/** Icon-Knopf wie die Leiste im Hauptreiter. */
const SUCHE_ICON =
  'portal-suche-icon inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200 hover:bg-violet-600/55 disabled:cursor-not-allowed disabled:opacity-45';
const SUCHE_ICON_ON =
  'portal-suche-icon-on inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-violet-400 bg-violet-600 text-white disabled:cursor-not-allowed disabled:opacity-45';

/**
 * Dieselbe Würfel-Drehung wie Arko ON/off: rotateX, 500ms, Perspektive 280.
 * Nur in einer group-Klappleiste (details.group).
 */
function SucheCube({
  front,
  back,
}: {
  front: React.ReactNode;
  back: React.ReactNode;
}) {
  const face =
    'absolute inset-0 inline-flex h-full w-full items-center justify-center [backface-visibility:hidden]';
  return (
    <span className="relative block h-7 w-7 shrink-0 [perspective:280px]" aria-hidden>
      <span className="relative block h-full w-full transition-transform duration-500 [transform-style:preserve-3d] group-open:rotate-x-180">
        <span className={`${face} portal-suche-nachweis-face rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200`}>
          {front}
        </span>
        <span
          className={`${face} portal-suche-nachweis-face rounded-md bg-violet-600 text-white`}
          style={{ transform: 'rotateX(180deg)' }}
        >
          {back}
        </span>
      </span>
    </span>
  );
}

function SucheCiteIdentity({
  label,
  testId,
  accent = 'violet',
}: {
  label: string;
  testId?: string;
  accent?: 'violet' | 'amber' | 'emerald';
}) {
  const tone =
    accent === 'amber'
      ? 'border-amber-300/70 bg-amber-500/20 text-amber-50'
      : accent === 'emerald'
        ? 'border-emerald-300/70 bg-emerald-500/20 text-emerald-50'
        : 'border-violet-400/40 bg-[#07070a] text-violet-100';
  return (
    <span
      data-testid={testId}
      title={label}
      className={`inline-flex items-center shrink-0 rounded-lg border px-3 py-2.5 text-2xl font-semibold font-mono min-h-[4.25rem] min-w-[8.5rem] max-w-[12.5rem] justify-center tracking-wide text-center leading-tight ${tone}`}
    >
      {label}
    </span>
  );
}

function SucheMarkPin({
  markKey,
  marked,
  onToggleMarked,
}: {
  markKey: string;
  marked: boolean;
  onToggleMarked: (key: string) => void;
}) {
  return (
    <button
      type="button"
      data-testid={`portal-suche-hit-check-${markKey}`}
      aria-pressed={marked}
      aria-label={`${markKey} ${marked ? PORTAL_SUCHE_MARKED_PIN_LABEL : PORTAL_SUCHE_MARK_PIN_LABEL}`}
      title={marked ? PORTAL_SUCHE_MARKED_PIN_LABEL : PORTAL_SUCHE_MARK_PIN_LABEL}
      onClick={() => onToggleMarked(markKey)}
      className={marked ? SUCHE_ICON_ON : SUCHE_ICON}
    >
      <Pin className={`h-3.5 w-3.5 ${marked ? 'fill-current' : ''}`} aria-hidden />
    </button>
  );
}

/** Links Cite-Spalte (§, darunter Merken); rechts Titel + Fließtext. Merken nie in der Titelzeile. */
function SucheHitRail({
  cite,
  pin,
  children,
}: {
  cite: React.ReactNode;
  pin?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-4 sm:gap-6 w-full" data-testid="portal-suche-hit-rail">
      <div
        className="flex flex-col items-stretch gap-2 shrink-0"
        data-testid="portal-suche-hit-cite-rail"
      >
        {cite}
        {pin}
      </div>
      <div className="min-w-0 flex-1 space-y-3" data-testid="portal-suche-hit-copy">
        {children}
      </div>
    </div>
  );
}

/** Absatz-Karten nur aus dem gelieferten Wortlaut. Ziffern bleiben im Absatz. */
function SucheWortlautAbsaetze({ text }: { text: string }) {
  const blocks = portalSucheWortlautAbsaetze(text);
  if (!blocks.length) return null;
  return (
    <div data-testid="portal-suche-hit-wortlaut" className="space-y-2">
      <p className="text-sm font-semibold text-slate-200">Wortlaut</p>
      <div className="space-y-2" data-testid="portal-suche-wortlaut-absaetze">
        {blocks.map((block, index) => (
          <article
            key={`${block.nummer || 'block'}-${index}`}
            data-testid="portal-suche-wortlaut-absatz"
            data-absatz={block.nummer}
            className="rounded-lg border border-violet-400/30 bg-[#07070a] px-3 py-2.5"
          >
            {block.nummer ? (
              <p
                className="mb-1 font-mono text-sm font-semibold text-violet-100"
                data-testid="portal-suche-wortlaut-absatz-nr"
              >
                ({block.nummer})
              </p>
            ) : null}
            <div className="space-y-1 text-sm leading-relaxed text-slate-100">
              {block.zeilen.map((line, lineIndex) =>
                line.text === '' ? (
                  <div key={lineIndex} className="h-2" aria-hidden />
                ) : (
                  <p
                    key={lineIndex}
                    data-zeile={line.kind}
                    className={line.kind === 'ziffer' ? 'pl-6' : undefined}
                  >
                    {line.text}
                  </p>
                ),
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

/** Überschrift und Wortlaut nur aus Feldern, die der Treffer schon hat. */
function SucheHitReading({
  paragraph,
  title,
  wortlaut,
  fact,
  runText,
  absatzKarten = false,
  arkoEntitled = false,
}: {
  paragraph: string;
  title?: string;
  wortlaut?: string;
  fact?: string;
  /** Einordnung aus dem Lauf. Leer → Titel/Kopf, kein erfundener Satz. */
  runText?: string;
  /** Normen-Karte: Absätze des gelieferten Wortlauts. Judikatur bleibt ein Block. */
  absatzKarten?: boolean;
  /** true nur bei ARKO-Freischaltung über PORTAL_BAUSTEIN_FREISCHALTUNG. */
  arkoEntitled?: boolean;
}) {
  const summary = portalSucheCardSummary(title || '', paragraph, wortlaut || '', runText || '', {
    entitled: arkoEntitled === true,
  });
  const law = String(wortlaut || '').trim();
  const aside = String(fact || '').trim();
  const head = absatzKarten
    ? portalSucheHeadExplanation(title || '', paragraph, wortlaut || '')
    : '';
  if (!summary && !law && !aside && !head) return null;
  const factLine = aside ? (
    <p
      className="portal-para-quelle rounded-md border border-violet-400/30 bg-[#07070a] px-2 py-1.5 text-sm font-semibold text-slate-100"
      data-testid="portal-suche-hit-fact"
    >
      {aside}
    </p>
  ) : null;
  return (
    <div className="space-y-3 min-w-0 w-full">
      {summary ? (
        <div data-testid="portal-suche-hit-erklaerung">
          <p className="text-sm font-semibold text-slate-200">{PORTAL_SUCHE_MARKED_ORIENTATION_LEAD}</p>
          <p className="mt-1 text-sm text-slate-100 leading-relaxed" data-testid="portal-suche-hit-zusammenfassung">
            {summary}
          </p>
        </div>
      ) : null}
      {head ? (
        <p className="text-base font-semibold text-slate-50" data-testid="portal-suche-hit-titel">
          {head}
        </p>
      ) : null}
      {absatzKarten ? factLine : null}
      {law && absatzKarten ? <SucheWortlautAbsaetze text={law} /> : null}
      {law && !absatzKarten ? (
        <div data-testid="portal-suche-hit-wortlaut">
          <p className="text-sm font-semibold text-slate-200">Wortlaut</p>
          <p className="text-sm text-slate-100 leading-relaxed whitespace-pre-wrap">{law}</p>
        </div>
      ) : null}
      {!absatzKarten ? factLine : null}
    </div>
  );
}

function sucheCardShell(marked: boolean, fill: boolean, tone: string): string {
  return `w-full rounded-xl border px-4 py-4 sm:px-5 sm:py-5 ${fill ? 'min-h-[61vh]' : ''} ${
    marked ? 'border-violet-400/70 bg-violet-500/15 ring-1 ring-violet-300/40' : tone
  }`;
}

function ExploreHitCard({
  item,
  onOpen,
  marked,
  onToggleMarked,
  fill = false,
  runText = '',
  arkoEntitled = false,
}: {
  item: PortalSucheExploreHit;
  onOpen: (citeQuery: string) => void;
  marked: boolean;
  onToggleMarked: (key: string) => void;
  fill?: boolean;
  runText?: string;
  arkoEntitled?: boolean;
}) {
  const wortlaut = String(item.wortlaut || item.short || '').trim();
  const fact = quelleGueltigAbLine(item.validFrom);
  const crossRefs = portalSucheCrossRefChips(item.normChain, item.key);
  const risHref = portalSucheExternalRisHref(item.risUrl);
  return (
    <article
      className={sucheCardShell(marked, fill, 'border-violet-400/30 bg-[#07070a]')}
      data-testid="portal-suche-hit-card"
    >
      <SucheHitRail
        cite={
          <SucheCiteIdentity
            label={item.paragraph}
            testId={`portal-suche-hit-cite-${item.key}`}
          />
        }
        pin={<SucheMarkPin markKey={item.key} marked={marked} onToggleMarked={onToggleMarked} />}
      >
        <SucheHitReading
          paragraph={item.paragraph}
          title={item.title}
          wortlaut={wortlaut}
          fact={fact}
          runText={runText}
          absatzKarten
          arkoEntitled={arkoEntitled}
        />
        {crossRefs.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5" data-testid="portal-suche-norm-chain">
            {crossRefs.map((chip) => (
              <li key={chip.normId}>
                <button
                  type="button"
                  data-testid={`portal-suche-norm-chain-${chip.normId}`}
                  onClick={() => onOpen(chip.query)}
                  className="rounded-md border border-violet-400/40 bg-[#07070a] px-2 py-1 text-[11px] text-violet-100 hover:bg-violet-600/30"
                >
                  {chip.label}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {risHref ? (
          <a
            href={risHref}
            target="_blank"
            rel="noreferrer"
            className="portal-para-quelle inline-flex items-center gap-1 rounded-md border border-violet-400/30 bg-[#07070a] px-2 py-1.5 text-sm font-semibold text-slate-100 underline underline-offset-2"
          >
            <ExternalLink className="w-3.5 h-3.5" aria-hidden />
            {PORTAL_SUCHE_RIS_LABEL}
          </a>
        ) : null}
      </SucheHitRail>
    </article>
  );
}

function JudCaseCard({
  item,
  marked,
  onToggleMarked,
  fill = false,
  runText = '',
  arkoEntitled = false,
}: {
  item: JudicatureProofCase;
  marked?: boolean;
  onToggleMarked?: (key: string) => void;
  fill?: boolean;
  runText?: string;
  arkoEntitled?: boolean;
}) {
  const core = item.kernsaetze?.length ? item.kernsaetze : item.rechtssaetze;
  const selectable = typeof onToggleMarked === 'function';
  const wortlaut = (core || []).map((line) => String(line || '').trim()).filter(Boolean).join('\n\n');
  const facts = [
    item.date || '',
    item.norm_refs?.length ? `${PORTAL_SUCHE_NORM_REFS_LABEL}: ${item.norm_refs.join(', ')}` : '',
  ].filter(Boolean);
  return (
    <article
      className={sucheCardShell(Boolean(marked), fill, 'border-violet-400/30 bg-[#07070a]')}
      data-testid="portal-suche-hit-card"
    >
      <SucheHitRail
        cite={
          <SucheCiteIdentity
            label={item.geschaeftszahl}
            testId={`portal-suche-hit-cite-${item.case_id}`}
          />
        }
        pin={
          selectable ? (
            <SucheMarkPin markKey={item.case_id} marked={Boolean(marked)} onToggleMarked={onToggleMarked} />
          ) : undefined
        }
      >
        <SucheHitReading
          paragraph={item.geschaeftszahl}
          title={item.leitsatz}
          wortlaut={wortlaut}
          fact={facts.join(' · ')}
          runText={runText}
          arkoEntitled={arkoEntitled}
        />
        <a
          href={item.source_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-violet-300/90 hover:text-violet-100"
        >
          <ExternalLink className="w-3.5 h-3.5" aria-hidden />
          {PORTAL_SUCHE_RIS_OPEN_LABEL}
        </a>
      </SucheHitRail>
    </article>
  );
}

function SucheMarkedComposeBar({
  markedCount,
  busy,
  onCompose,
}: {
  markedCount: number;
  busy: boolean;
  onCompose: () => void;
}) {
  const enabled = portalSucheComposeButtonEnabled(markedCount, busy);
  return (
    <div
      className={`sticky top-0 z-20 flex flex-wrap items-center gap-2 px-3 py-2 ${SUCHE_EDGE}`}
      data-testid="portal-suche-marked-bar"
    >
      <button
        type="button"
        disabled={!enabled}
        data-testid="portal-suche-compose-marked-hits"
        onClick={() => onCompose()}
        aria-label={portalSucheComposeButtonLabel(markedCount)}
        title={portalSucheComposeButtonLabel(markedCount)}
        className={SUCHE_ICON}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <BookOpen className="h-3.5 w-3.5" aria-hidden />}
      </button>
      <p className="text-[12px] text-slate-200">
        {markedCount > 0 ? PORTAL_SUCHE_MARKED_BAR_SELECTED : PORTAL_SUCHE_MARKED_BAR_HINT}
      </p>
    </div>
  );
}

function SucheResultFolds({
  path,
  text,
  lexProof,
  judProof,
}: {
  path: PortalSucheComposePath | null;
  text: string;
  lexProof: LexProof | null;
  judProof: JudicatureProof | null;
}) {
  if (!path && !lexProof && !judProof) return null;
  return (
    <div
      className="flex flex-wrap items-start gap-x-3 gap-y-1"
      data-testid="portal-suche-result-folds"
    >
      {lexProof || judProof ? (
        <div className="min-w-0" data-testid="portal-suche-lex-proof">
          <ArkoResponseProof
            text={text}
            lexProof={lexProof}
            judProof={judProof}
            className="mt-0 max-w-none group"
            summaryLabel={PORTAL_SUCHE_PROOF_LABEL}
            lexSectionLabel={PORTAL_SUCHE_PROOF_LEX_SECTION}
            lawyerFacing
            akteLook
          />
        </div>
      ) : null}
      {path ? <SucheComposePathFold path={path} /> : null}
    </div>
  );
}

function SucheComposePathFold({ path }: { path: PortalSucheComposePath }) {
  return (
    <details className="group mt-0" data-testid="portal-suche-compose-path">
      <summary
        className="list-none cursor-pointer select-none flex items-center gap-1"
        aria-label={PORTAL_SUCHE_MARKED_PATH_LABEL}
      >
        <SucheCube
          front={<Route className="h-3.5 w-3.5" aria-hidden />}
          back={<Route className="h-3.5 w-3.5" aria-hidden />}
        />
        <span className="sr-only">{PORTAL_SUCHE_MARKED_PATH_LABEL}</span>
        <span className="portal-suche-nachweis-chip text-[11px] font-semibold text-violet-100">
          {portalSuchePathChipCountLabel(path.chips.length)}
        </span>
      </summary>
      <div className="mt-2 pl-1 space-y-2 border-l border-violet-400/30 ml-1" data-testid="portal-suche-compose-path-body">
        <p className="text-sm text-slate-300">{PORTAL_SUCHE_MARKED_PATH_KIND}</p>
        <p className="text-sm text-slate-400" data-testid="portal-suche-compose-path-rao">
          {path.disclaimer}
        </p>
        <ol className="space-y-1" data-testid="portal-suche-compose-path-steps">
          {(path.steps?.length ? path.steps : portalSucheComposePathSteps(path)).map((step) => (
            <li key={step} className="text-sm text-slate-200 leading-relaxed">
              {step}
            </li>
          ))}
        </ol>
        <ol className="space-y-1" data-testid="portal-suche-compose-path-chips">
          <li className="text-sm text-slate-200 leading-relaxed" data-testid="portal-suche-compose-path-pinned">
            {path.pinnedLabel}
          </li>
          {path.chips.map((chip) => (
            <li
              key={chip.key}
              className="text-sm text-slate-200 leading-relaxed"
              data-testid={`portal-suche-compose-path-chip-${chip.key}`}
              data-path-status={chip.status}
            >
              <span className="font-medium text-slate-200">
                {PORTAL_SUCHE_MARKED_PATH_RETRIEVE} {chip.label}
              </span>
              {': '}
              {chip.status === 'hit' ? PORTAL_SUCHE_MARKED_PATH_HIT : PORTAL_SUCHE_MARKED_PATH_SKIP_STATUS}
              {' · '}
              <span className="font-mono text-[10px] text-slate-400">{chip.source}</span>
            </li>
          ))}
          <li className="text-sm text-slate-200 leading-relaxed" data-testid="portal-suche-compose-path-compose">
            {path.composeClose}
          </li>
        </ol>
      </div>
    </details>
  );
}

function SucheComposeChipBlock({
  block,
  fill = false,
  arkoEntitled = false,
}: {
  block: PortalSucheComposeBlock;
  fill?: boolean;
  arkoEntitled?: boolean;
}) {
  const skip = block.status === 'skip';
  const fact = !skip ? quelleGueltigAbLine(block.validFrom) : '';
  return (
    <article
      className={sucheCardShell(
        false,
        fill,
        'border-violet-400/30 bg-[#07070a]',
      )}
      data-testid={`portal-suche-compose-block-${block.key}`}
      data-compose-status={block.status}
    >
      <SucheHitRail
        cite={
          <SucheCiteIdentity
            label={block.paragraph}
            testId={`portal-suche-compose-cite-${block.key}`}
            accent={skip ? 'amber' : 'emerald'}
          />
        }
      >
        {skip ? (
          <p className="text-sm text-amber-100 leading-relaxed">{block.body}</p>
        ) : (
          <SucheHitReading
            paragraph={block.paragraph}
            title={block.title}
            wortlaut={block.body}
            fact={fact}
            runText={block.summary}
            absatzKarten
            arkoEntitled={arkoEntitled}
          />
        )}
        {block.risUrl ? (
          <a
            href={block.risUrl}
            target="_blank"
            rel="noreferrer"
            className="portal-para-quelle inline-flex items-center gap-1 rounded-md border border-violet-400/30 bg-[#07070a] px-2 py-1.5 text-sm font-semibold text-slate-100 underline underline-offset-2"
          >
            <ExternalLink className="w-3.5 h-3.5" aria-hidden />
            {PORTAL_SUCHE_RIS_LABEL}
          </a>
        ) : null}
      </SucheHitRail>
    </article>
  );
}

export const SucheView: React.FC<SucheViewProps> = ({ webhookUrl, extraBody, seat }) => {
  const sucheLight = portalSucheSurfaceClass(usePortalAppearance());
  const arkoEntitled = bausteinRowIsGreen(resolveBausteinUnlock('ARKO', seat ?? {}));
  const judicatureOpen = judicatureChipVisible(seat ?? {});
  const sucheTabs = portalSucheTabs(judicatureOpen);
  const persistSeat = portalSuchePersistSeatId(extraBody);
  const persistBootRef = useRef<PortalSuchePersistedState | null>(null);
  if (persistBootRef.current === null) {
    persistBootRef.current = loadPortalSuchePersist(persistSeat);
  }
  const persistBoot = persistBootRef.current;
  const [kind, setKind] = useState<PortalSucheKind>(persistBoot.kind);
  useEffect(() => {
    if (!judicatureOpen && kind === 'jud') setKind('lex');
  }, [judicatureOpen, kind]);
  const [query, setQuery] = useState(persistBoot.query || persistBoot.search?.query || '');
  const [queryChips, setQueryChips] = useState<PortalSucheQueryChip[]>(persistBoot.chips);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');
  const [hit, setHit] = useState<PortalSucheHit | null>(() => sucheSearchHitFromPersist(persistBoot));
  const [markedKeys, setMarkedKeys] = useState<string[]>([]);
  const [composeHit, setComposeHit] = useState<PortalSucheHit | null>(() =>
    persistBoot.view === 'compose' ? sucheHitFromPersist(persistBoot) : null,
  );
  const abortRef = useRef<AbortController | null>(null);
  const persistSkipSaveRef = useRef(true);
  const persistSeatRef = useRef(persistSeat);
  const typedMode = portalSucheMode(kind, query);

  useEffect(() => {
    if (persistSeatRef.current === persistSeat) return;
    persistSeatRef.current = persistSeat;
    const state = loadPortalSuchePersist(persistSeat);
    persistBootRef.current = state;
    setKind(state.kind);
    setQuery(state.query || state.search?.query || '');
    setQueryChips(state.chips);
    setHit(sucheSearchHitFromPersist(state));
    setComposeHit(state.view === 'compose' ? sucheHitFromPersist(state) : null);
    persistSkipSaveRef.current = true;
  }, [persistSeat]);

  useEffect(() => {
    if (persistSkipSaveRef.current) {
      persistSkipSaveRef.current = false;
      return;
    }
    const showingCompose = Boolean(composeHit);
    savePortalSuchePersist(
      portalSuchePersistFromView({
        kind,
        query: showingCompose ? '' : hit?.query || query,
        view: showingCompose ? 'compose' : 'search',
        chips: queryChips,
        search: showingCompose ? null : portalSucheSearchSnapshotFromHit(hit),
        compose: portalSucheComposeSnapshotFromHit(composeHit),
      }),
      persistSeat,
    );
  }, [composeHit, hit, kind, persistSeat, query, queryChips]);

  const suggestions = useMemo(
    () => (kind === 'lex' ? suggestParagraphRefs(query, 6) : []),
    [kind, query],
  );
  const parsed = useMemo(
    () => (kind === 'lex' ? parseParagraphQuery(query) : []),
    [kind, query],
  );

  const runSearch = useCallback(
    async (rawQuery: string, nextKind: PortalSucheKind = kind) => {
      if (nextKind === 'jud' && !judicatureOpen) return;
      const nextMode = portalSucheMode(nextKind, rawQuery);
      const body = buildPortalSucheWebhookBody({
        kind: nextKind,
        query: rawQuery,
        extraBody,
        requestId: newPortalSucheRequestId(),
      });
      if (!body) {
        setError(
          nextKind === 'jud' ? PORTAL_SUCHE_ERROR_EMPTY_JUD : PORTAL_SUCHE_ERROR_EMPTY_LEX,
        );
        return;
      }
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const timeout = window.setTimeout(() => controller.abort(), PORTAL_SUCHE_TIMEOUT_MS);
      setBusy(true);
      setError('');
      setHit(null);
      setComposeHit(null);
      setMarkedKeys([]);
      setDraft('');
      /* Leisten-Chips bleiben — Pin vor der Auswertung, Einzelsuche löscht sie nicht. */
      try {
        const requestId = String(body.request_id);
        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Request-Id': requestId,
            ...portalAuthzHeaders(webhookUrl),
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });
        if (!response.ok) {
          setError(portalSucheHttpErrorMessage(response.status));
          return;
        }
        const data: unknown = await response.json();
        const streamPath = portalSucheStreamPath(data);
        if (streamPath) {
          const streamUrl = streamPath.startsWith('http')
            ? streamPath
            : `${window.location.origin}${streamPath.startsWith('/') ? '' : '/'}${streamPath}`;
          let draftText = '';
          let gotFinal = false;
          await consumeArkoSse(streamUrl, portalAuthzHeaders(streamUrl), controller.signal, {
            onLexSources: (lexData) => {
              const parsed = parseLexProof(lexData);
              const proof = nextMode === 'explore'
                ? applyKeywordNormProof(rawQuery, parsed)
                : parsed;
              if (!proof) return;
              setHit((prev) => ({
                kind: nextKind,
                mode: nextMode,
                query: rawQuery,
                text: prev?.text || '',
                lexProof: proof,
                judProof: prev?.judProof || null,
              }));
            },
            onToken: (piece) => {
              draftText += piece;
              if (nextMode !== 'explore') setDraft(draftText);
            },
            onFinal: (finalPayload) => {
              gotFinal = true;
              const next = toSucheHit(nextKind, nextMode, finalPayload, draftText, rawQuery);
              setHit((prev) => {
                const prevSources = prev?.lexProof?.sources?.length ?? 0;
                const nextSources = next.lexProof?.sources?.length ?? 0;
                if (
                  nextMode === 'explore' &&
                  isKeywordNormQuery(rawQuery) &&
                  nextSources === 0
                ) {
                  return next;
                }
                if (nextMode === 'explore' && prevSources > 0 && nextSources === 0) {
                  return { ...next, lexProof: prev?.lexProof ?? next.lexProof };
                }
                const prevCases = prev?.judProof?.cases?.length ?? 0;
                const nextCases = next.judProof?.cases?.length ?? 0;
                if (nextMode === 'explore' && prevCases > 0 && nextCases === 0) {
                  return { ...next, judProof: prev?.judProof ?? next.judProof };
                }
                return next;
              });
            },
            onError: (code) => {
              setError(PORTAL_SUCHE_ERROR_ABORTED);
            },
          });
          if (!gotFinal && !controller.signal.aborted) {
            setHit(
              toSucheHit(nextKind, nextMode, unwrapPortalArkoWebhookPayload(data), draftText, rawQuery),
            );
          }
          return;
        }
        setHit(toSucheHit(nextKind, nextMode, data, '', rawQuery));
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') {
          setError(PORTAL_SUCHE_ERROR_TIMEOUT);
          return;
        }
        setError(PORTAL_SUCHE_ERROR_UNREACHABLE);
      } finally {
        window.clearTimeout(timeout);
        if (abortRef.current === controller) abortRef.current = null;
        setBusy(false);
      }
    },
    [extraBody, judicatureOpen, kind, webhookUrl],
  );

  const toggleMarked = useCallback((key: string) => {
    setMarkedKeys((prev) => portalSucheToggleMarkedKeys(prev, key));
  }, []);

  const pinCurrentQuery = useCallback(() => {
    setQueryChips((prev) => {
      const next = portalSuchePinQueryChip(prev, kind, query);
      return next;
    });
    if (!query.trim()) return;
    setQuery('');
    setComposeHit(null);
    setError('');
  }, [kind, query]);

  const removeQueryChip = useCallback((key: string) => {
    setQueryChips((prev) => portalSucheRemoveQueryChip(prev, key));
    setComposeHit(null);
  }, []);

  const runMarkedCompose = useCallback(async () => {
    const currentHits =
      hit?.kind === 'jud'
        ? portalSucheExploreHitsFromJudCases(hit.judProof)
        : portalSucheExploreHitsFromLexSources(hit?.lexProof);
    const selected = portalSucheMergeMarkedHits(
      portalSucheChipsToExploreHits(queryChips),
      portalSucheMarkedHits(currentHits, markedKeys),
    );
    if (!selected.length) {
      setError(PORTAL_SUCHE_MARKED_EMPTY_HINT);
      setComposeHit(null);
      return;
    }
    const nextKind = hit?.kind ?? kind;
    const chipBodies = buildPortalSucheMarkedChipWebhookBodies({
      kind: nextKind,
      selected,
      extraBody,
    });
    if (!chipBodies.length) {
      setError(PORTAL_SUCHE_MARKED_EMPTY_HINT);
      setComposeHit(null);
      return;
    }
    const chipByKey = new Map(queryChips.map((chip) => [chip.key, chip]));
    const filterLexForHit = (rawLex: unknown, item: PortalSucheExploreHit) => {
      const chip = chipByKey.get(item.key);
      if (chip) return filterPortalSucheLexProofToMarkedChips(rawLex, [chip]);
      return filterPortalSucheLexProofToMarkedIds(rawLex, [item.key]);
    };
    const filterJud = (rawJud: unknown) =>
      queryChips.length
        ? filterPortalSucheJudProofToMarkedChips(rawJud, queryChips)
        : filterPortalSucheJudProofToMarkedKeys(
            rawJud,
            selected.map((item) => item.key),
          );
    const toComposeHit = (
      mergedLex: unknown,
      mergedJud: unknown,
      summariesByKey: Record<string, string>,
    ): PortalSucheHit => {
      const lexProof = parseLexProof(mergedLex);
      const judProof = parseJudicatureProof(mergedJud);
      const filteredSources = (lexProof?.sources ?? []).map((src) => ({
        norm_id: src.norm_id,
        title: src.title,
        fragment_text: src.fragment_text,
        ris_url: src.ris_url,
      }));
      const judCases = judProof?.cases ?? [];
      const composeBlocks = portalSucheComposeBlocks({
        selected,
        filteredSources,
        judCases,
        summariesByKey,
      });
      const modelText = Object.values(summariesByKey).filter(Boolean).join('\n\n');
      return {
        kind: nextKind,
        mode: 'cite',
        query: selected.map((item) => item.citeQuery || item.paragraph).filter(Boolean).join(', '),
        text: buildPortalSucheMarkedOrientation({
          selected,
          filteredSources,
          judCases,
          modelText,
          entitled: arkoEntitled,
        }),
        lexProof,
        judProof,
        composeBlocks,
        composePath: buildPortalSucheComposePath({
          selected,
          blocks: composeBlocks,
          filteredSources,
          judCases,
        }),
      };
    };
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const timeout = window.setTimeout(() => controller.abort(), PORTAL_SUCHE_TIMEOUT_MS);
    setBusy(true);
    setError('');
    setComposeHit(null);
    const fetchOnePayload = async (body: Record<string, unknown>): Promise<unknown> => {
      const requestId = String(body.request_id);
      let draftText = '';
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Request-Id': requestId,
          ...portalAuthzHeaders(webhookUrl),
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!response.ok) {
        throw Object.assign(new Error(portalSucheHttpErrorMessage(response.status)), {
          httpStatus: response.status,
        });
      }
      const data: unknown = await response.json();
      const streamPath = portalSucheStreamPath(data);
      if (!streamPath) return data;
      const streamUrl = streamPath.startsWith('http')
        ? streamPath
        : `${window.location.origin}${streamPath.startsWith('/') ? '' : '/'}${streamPath}`;
      let finalPayload: unknown = unwrapPortalArkoWebhookPayload(data);
      let gotFinal = false;
      await consumeArkoSse(streamUrl, portalAuthzHeaders(streamUrl), controller.signal, {
        onLexSources: (lexData) => {
          const prev = unwrapPortalArkoWebhookPayload(finalPayload);
          finalPayload = { ...prev, lexProof: lexData };
        },
        onToken: (piece) => {
          draftText += piece;
        },
        onFinal: (payload) => {
          gotFinal = true;
          finalPayload = payload;
        },
        onError: (code) => {
          throw Object.assign(new Error(PORTAL_SUCHE_ERROR_ABORTED), { streamCode: code });
        },
      });
      if (!gotFinal && controller.signal.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }
      return finalPayload;
    };
    try {
      const settled = await Promise.allSettled(
        chipBodies.map(async (row) => {
          const payload = await fetchOnePayload(row.body);
          const hitRow = selected.find((item) => item.key === row.key) || selected[0];
          const raw = parsePortalSucheHitRaw(nextKind, payload, draftText);
          return {
            key: row.key,
            lex: filterLexForHit(raw.lexProof, hitRow),
            jud: raw.judProof,
            runText: raw.text,
          };
        }),
      );
      if (controller.signal.aborted) {
        setError(PORTAL_SUCHE_ERROR_TIMEOUT);
        return;
      }
      const ok = settled.filter(
        (row): row is PromiseFulfilledResult<{
          key: string;
          lex: unknown;
          jud: unknown;
          runText: string;
        }> => row.status === 'fulfilled',
      );
      if (!ok.length) {
        const firstFail = settled.find((row) => row.status === 'rejected') as
          | PromiseRejectedResult
          | undefined;
        const reason = firstFail?.reason as { httpStatus?: number; name?: string; message?: string };
        if (reason?.name === 'AbortError') {
          setError(PORTAL_SUCHE_ERROR_TIMEOUT);
          return;
        }
        if (typeof reason?.httpStatus === 'number') {
          setError(portalSucheHttpErrorMessage(reason.httpStatus));
          return;
        }
        setError(
          String(reason?.message || '').trim() ||
            PORTAL_SUCHE_ERROR_UNREACHABLE,
        );
        return;
      }
      const mergedLex = mergePortalSucheLexProofs(ok.map((row) => row.value.lex));
      const mergedJud = filterJud(ok[0]?.value.jud);
      const summariesByKey: Record<string, string> = {};
      for (const row of ok) {
        if (row.value.runText) summariesByKey[row.value.key] = row.value.runText;
      }
      setComposeHit(toComposeHit(mergedLex, mergedJud, summariesByKey));
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') {
        setError(PORTAL_SUCHE_ERROR_TIMEOUT);
        return;
      }
      setError(PORTAL_SUCHE_ERROR_UNREACHABLE);
    } finally {
      window.clearTimeout(timeout);
      if (abortRef.current === controller) abortRef.current = null;
      setBusy(false);
    }
  }, [arkoEntitled, extraBody, hit, kind, markedKeys, queryChips, webhookUrl]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const action = portalSucheEnterAction(queryChips.length, query);
    if (action === 'pin') {
      pinCurrentQuery();
      return;
    }
    if (action === 'compose') {
      void runMarkedCompose();
      return;
    }
    void runSearch(query, kind);
  };

  const resultMode = hit?.mode ?? typedMode;
  const showingCompose = Boolean(composeHit);
  const exploreHits: PortalSucheExploreHit[] =
    hit?.kind === 'jud'
      ? portalSucheExploreHitsFromJudCases(hit.judProof)
      : portalSucheExploreHitsFromLexSources(hit?.lexProof);
  const showExploreList = resultMode === 'explore';
  const displayText =
    showingCompose || showExploreList
      ? ''
      : hit
        ? formatBotMessageForDisplay(
            hit.kind === 'jud' ? stripJudicatureStampForDisplay(hit.text) : hit.text,
            hit.lexProof,
          )
        : draft;
  const judCases = hit?.judProof?.cases ?? [];
  const showEmptyExplore =
    !showingCompose && Boolean(hit) && showExploreList && exploreHits.length === 0 && !busy;
  const showLexOrExploreCards =
    !showingCompose && exploreHits.length > 0 && (showExploreList || hit?.kind !== 'jud');
  const showJudCiteCards =
    !showingCompose && Boolean(hit && !showExploreList && hit.kind === 'jud' && judCases.length > 0);
  const showMarkedBar = !showingCompose && (showLexOrExploreCards || showJudCiteCards);
  const composeBlocks = composeHit?.composeBlocks ?? [];
  const lexCardsHaveWortlaut =
    showLexOrExploreCards && exploreHits.some((item) => String(item.wortlaut || '').trim().length > 0);
  const fillScreen =
    composeBlocks.length === 1 ||
    (!showingCompose &&
      !showExploreList &&
      ((showLexOrExploreCards && exploreHits.length === 1) ||
        (showJudCiteCards && judCases.length === 1)));
  const answerPath: PortalSucheComposePath | null = composeHit?.composePath
    ? composeHit.composePath
    : hit && !showExploreList && (displayText || hit.text)
      ? buildPortalSucheAnswerPath({
          kind: hit.kind,
          query: hit.query || query,
          filteredSources: portalSucheComposeSourcesFromLexProof(hit.lexProof),
          judCases: hit.judProof?.cases,
        })
      : null;
  const answerRunText = showingCompose ? '' : String(hit?.text || draft || '');
  const multiFragments = showJudCiteCards
    ? judCases.flatMap((item) => {
        const core = item.kernsaetze?.length ? item.kernsaetze : item.rechtssaetze;
        return (core || []).map((line) => String(line || ''));
      })
    : exploreHits.map((item) => item.wortlaut || item.short || '');
  const multiCount = showJudCiteCards ? judCases.length : exploreHits.length;
  const multiSource = [
    ...multiFragments,
    ...exploreHits.map((item) => `${item.paragraph} ${item.key} ${item.title || ''}`),
    ...judCases.map((item) => `${item.geschaeftszahl} ${item.case_id}`),
  ].join('\n');
  const multiLead =
    !showingCompose && multiCount > 1
      ? portalSucheOrientationModelText({
          modelText: portalSucheRunSummaryText(answerRunText, multiFragments),
          entitled: arkoEntitled,
          sourceText: multiSource,
        })
      : '';
  const composeOrientation = composeHit
    ? portalSucheVisibleOrientation({
        text: composeHit.text,
        entitled: arkoEntitled,
        blocks: composeBlocks,
      })
    : '';
  const proofText = composeHit ? composeOrientation : hit?.text || displayText;
  const proofLex = composeHit?.lexProof ?? (!showExploreList ? hit?.lexProof ?? null : null);
  const proofJud = composeHit?.judProof ?? (!showExploreList ? hit?.judProof ?? null : null);

  const copyResult = async () => {
    const parts: string[] = [];
    if (composeHit) {
      if (composeOrientation) parts.push(composeOrientation);
      for (const block of composeHit.composeBlocks ?? []) {
        parts.push(
          [block.paragraph, block.title !== block.paragraph ? block.title : '', block.body, block.risUrl]
            .filter(Boolean)
            .join('\n'),
        );
      }
    } else if (showExploreList) {
      for (const item of exploreHits) {
        parts.push([item.paragraph, item.title, item.short, item.risUrl].filter(Boolean).join('\n'));
      }
    } else {
      if (displayText && !lexCardsHaveWortlaut && !showJudCiteCards) parts.push(displayText);
      for (const src of hit?.lexProof?.sources ?? []) {
        if (src.norm_id && src.fragment_text) {
          parts.push(`${src.norm_id}: ${src.fragment_text}`);
        }
      }
      for (const cse of judCases) {
        parts.push([cse.geschaeftszahl, cse.leitsatz, cse.source_url].filter(Boolean).join('\n'));
      }
    }
    const blob = parts.filter(Boolean).join('\n\n').trim();
    if (!blob) return;
    try {
      await navigator.clipboard.writeText(blob);
    } catch {
      setError(PORTAL_SUCHE_COPY_BLOCKED);
    }
  };

  const openExploreHit = (citeQuery: string) => {
    setQuery(citeQuery);
    void runSearch(citeQuery, kind);
  };

  return (
    <div className={`w-full min-w-0 max-w-none flex-1 overflow-y-auto space-y-3 min-h-[40vh] px-3 py-3 ${SUCHE_GROUND} ${sucheLight}`} data-testid="portal-suche">
      <span className="sr-only">{PORTAL_SUCHE_CLASS}</span>
      <span className="sr-only">{PORTAL_SUCHE_MARKED_CLASS}</span>
      <span className="sr-only">{PORTAL_JUD_SEARCH_FIELD_MARKER}</span>
      <span className="sr-only">{PORTAL_JUD_SEARCH_LAWYER_MARKER}</span>
      <section className={`space-y-3 px-3.5 py-3 ${SUCHE_EDGE}`}>
      <header>
        <h1 className="m-0 text-sm font-semibold text-white">{PORTAL_SUCHE_PAGE_TITLE}</h1>
        <p className="m-0 mt-0.5 text-[11px] text-slate-500">{PORTAL_SUCHE_PAGE_SUBTITLE}</p>
      </header>

      <div
        className="flex items-center gap-1"
        role="tablist"
        aria-label={PORTAL_SUCHE_TABLIST_LABEL}
      >
        {sucheTabs.map((tab) => {
          const active = kind === tab.id;
          const TabIcon = tab.id === 'jud' ? Scale : Search;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              aria-label={tab.label}
              title={tab.label}
              data-testid={`portal-suche-tab-${tab.id}`}
              onClick={() => {
                setKind(portalSucheKindFromTab(tab.id));
                setError('');
                setComposeHit(null);
                setMarkedKeys([]);
                setQueryChips([]);
              }}
              className={active ? SUCHE_ICON_ON : SUCHE_ICON}
            >
              <TabIcon className="h-3.5 w-3.5" aria-hidden />
              <span className="sr-only">{tab.label}</span>
            </button>
          );
        })}
      </div>

      <form onSubmit={onSubmit} className="space-y-2" data-testid="portal-suche-form">
        {queryChips.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5" data-testid="portal-suche-query-chips">
            {queryChips.map((chip) => (
              <li key={chip.key}>
                <span
                  data-testid={`portal-suche-query-chip-${chip.key}`}
                  className="inline-flex items-center gap-1 rounded-md border border-violet-400/40 bg-[#07070a] pl-2 pr-1 py-1 text-[11px] text-violet-100"
                >
                  <Pin className="w-3 h-3 fill-current" aria-hidden />
                  {chip.label}
                  <button
                    type="button"
                    data-testid={`portal-suche-query-chip-remove-${chip.key}`}
                    aria-label={`${chip.label} entfernen`}
                    disabled={busy}
                    onClick={() => removeQueryChip(chip.key)}
                    className="rounded-full p-0.5 text-violet-200 hover:bg-violet-500/40 hover:text-white disabled:opacity-40"
                  >
                    <X className="w-3.5 h-3.5" aria-hidden />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        <label className="block">
          <span className="sr-only">
            {kind === 'jud' ? PORTAL_SUCHE_INPUT_LABEL_JUD : PORTAL_SUCHE_INPUT_LABEL_LEX}
          </span>
          <div className="flex items-center gap-2 rounded-md border border-violet-400/30 bg-[#07070a] px-3 py-2 focus-within:border-violet-400">
            {kind === 'jud' ? (
              <Scale className="w-4 h-4 text-slate-500 shrink-0" aria-hidden />
            ) : (
              <Search className="w-4 h-4 text-slate-500 shrink-0" aria-hidden />
            )}
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                  e.preventDefault();
                  pinCurrentQuery();
                }
              }}
              disabled={busy}
              data-testid="portal-suche-input"
              placeholder={
                kind === 'jud' ? PORTAL_SUCHE_PLACEHOLDER_JUD : PORTAL_SUCHE_PLACEHOLDER_LEX
              }
              autoComplete="off"
              className="w-full bg-transparent text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none"
            />
            <button
              type="button"
              data-testid="portal-suche-pin-query"
              title={`${PORTAL_SUCHE_BAR_PIN_HINT} (Strg+Enter)`}
              aria-label={PORTAL_SUCHE_BAR_PIN_LABEL}
              disabled={busy || !query.trim()}
              onClick={() => pinCurrentQuery()}
              className={query.trim() && !busy ? SUCHE_ICON_ON : SUCHE_ICON}
            >
              <Pin className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        </label>
        <p
          className={`text-[11px] ${PORTAL_SHELL.textMuted}`}
          data-testid={typedMode === 'cite' ? 'portal-suche-mode-cite' : 'portal-suche-mode-explore'}
        >
          {kind === 'jud'
            ? typedMode === 'cite'
              ? PORTAL_SUCHE_MODE_HINT_JUD_CITE
              : PORTAL_SUCHE_MODE_HINT_JUD_EXPLORE
            : typedMode === 'cite'
              ? PORTAL_SUCHE_MODE_HINT_LEX_CITE
              : PORTAL_SUCHE_MODE_HINT_LEX_EXPLORE}
        </p>
        {kind === 'lex' && suggestions.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5" data-testid="portal-suche-lex-hints">
            {suggestions.map((ref) => (
              <li key={ref.key}>
                <button
                  type="button"
                  disabled={busy || ref.section === '…'}
                  onClick={() => {
                    if (ref.section === '…') {
                      setQuery(`${ref.law ?? ''} `);
                      return;
                    }
                    setQuery(ref.label);
                  }}
                  className="rounded-md border border-violet-400/40 bg-[#07070a] px-2 py-1 text-[11px] text-violet-100 hover:bg-violet-600/30 disabled:opacity-40"
                >
                  {ref.label}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {kind === 'lex' && parsed.length > 0 ? (
          <p className="text-[11px] text-slate-400">
            {PORTAL_SUCHE_RECOGNIZED_PREFIX}{' '}
            <span className="text-slate-200 font-medium">{parsed.map((r) => r.label).join(', ')}</span>
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={busy}
            data-testid="portal-suche-submit"
            onClick={() => void runSearch(query, kind)}
            className={SUCHE_BTN}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Search className="h-3.5 w-3.5" aria-hidden />}
            {PORTAL_SUCHE_SUBMIT_LABEL}
          </button>
          {queryChips.length > 0 ? (
            <button
              type="button"
              disabled={!portalSucheComposeButtonEnabled(queryChips.length, busy)}
              data-testid="portal-suche-compose-marked"
              onClick={() => void runMarkedCompose()}
              aria-label={portalSucheBarComposeButtonLabel(queryChips.length)}
              title={portalSucheBarComposeButtonLabel(queryChips.length)}
              className={SUCHE_ICON}
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <BookOpen className="h-3.5 w-3.5" aria-hidden />
              )}
            </button>
          ) : null}
        </div>
      </form>
      </section>

      {error ? (
        <p
          role="alert"
          data-testid="portal-suche-error"
          className="rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-sm text-amber-100"
        >
          {error}
        </p>
      ) : null}

      {busy && !hit && !displayText && !composeHit ? (
        <p className={`text-sm ${PORTAL_SHELL.textMuted}`} data-testid="portal-suche-pending">
          {PORTAL_SUCHE_PENDING}
        </p>
      ) : null}

      {displayText || hit || composeHit ? (
        <section
          className={`w-full space-y-4 px-3.5 py-3 ${SUCHE_EDGE} ${
            fillScreen ? 'min-h-[63vh]' : ''
          }`}
          data-testid="portal-suche-results"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-semibold text-white">
                {composeHit
                  ? PORTAL_SUCHE_HEADING_COMPOSE
                  : showExploreList
                    ? hit?.kind === 'jud'
                      ? PORTAL_SUCHE_HEADING_JUD_EXPLORE
                      : PORTAL_SUCHE_HEADING_LEX_EXPLORE
                    : hit?.kind === 'jud'
                      ? PORTAL_SUCHE_HEADING_JUD_CITE
                      : PORTAL_SUCHE_HEADING_LEX_CITE}
              </h2>
              {!showingCompose && !showExploreList && hit?.lexProof ? (
                <ArkoTrustBadge
                  text={hit.text}
                  lexProof={hit.lexProof}
                  verifiedTitle={PORTAL_SUCHE_TRUST_VERIFIED_TITLE}
                  unverifiedTitle={PORTAL_SUCHE_TRUST_UNVERIFIED_TITLE}
                  lawyerFacing
                  akteLook
                />
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => void copyResult()}
              className={SUCHE_ICON}
              aria-label={PORTAL_SUCHE_COPY_LABEL}
              title={PORTAL_SUCHE_COPY_LABEL}
              data-testid="portal-suche-copy"
            >
              <Copy className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
          {showMarkedBar ? (
            <SucheMarkedComposeBar
              markedCount={markedKeys.length}
              busy={busy}
              onCompose={() => void runMarkedCompose()}
            />
          ) : null}
          {!showingCompose && !showExploreList && displayText && !lexCardsHaveWortlaut && !showJudCiteCards ? (
            <div className="whitespace-pre-wrap text-sm text-slate-200 leading-relaxed">{displayText}</div>
          ) : null}
          {multiLead ? (
            <div data-testid="portal-suche-hit-erklaerung">
              <p className="text-sm font-semibold text-slate-200">{PORTAL_SUCHE_MARKED_ORIENTATION_LEAD}</p>
              <p
                className="mt-1 text-sm text-slate-100 leading-relaxed"
                data-testid="portal-suche-hit-zusammenfassung"
              >
                {multiLead}
              </p>
            </div>
          ) : null}
          {showLexOrExploreCards ? (
            <ul className="w-full space-y-4" data-testid="portal-suche-explore-hits">
              {exploreHits.map((item) => (
                <li key={item.key} className="w-full">
                  <ExploreHitCard
                    item={item}
                    onOpen={openExploreHit}
                    marked={markedKeys.includes(item.key)}
                    onToggleMarked={toggleMarked}
                    fill={fillScreen && exploreHits.length === 1}
                    runText={exploreHits.length === 1 ? answerRunText : ''}
                    arkoEntitled={arkoEntitled}
                  />
                </li>
              ))}
            </ul>
          ) : null}
          {showJudCiteCards ? (
            <div className="w-full space-y-4" data-testid="portal-suche-jud-cases">
              {judCases.map((item) => (
                <JudCaseCard
                  key={item.case_id}
                  item={item}
                  marked={markedKeys.includes(item.case_id)}
                  onToggleMarked={toggleMarked}
                  fill={fillScreen && judCases.length === 1}
                  runText={judCases.length === 1 ? answerRunText : ''}
                  arkoEntitled={arkoEntitled}
                />
              ))}
            </div>
          ) : null}
          {showEmptyExplore ? (
            <p className={`text-sm ${PORTAL_SHELL.textMuted}`} data-testid="portal-suche-empty">
              {PORTAL_SUCHE_EMPTY_EXPLORE}
            </p>
          ) : null}
          {composeHit ? (
            <div
              className={`w-full space-y-4 px-3.5 py-3 ${SUCHE_EDGE}`}
              data-testid="portal-suche-marked-compose"
            >
              <h3
                className="text-sm font-semibold text-white"
                data-testid="portal-suche-marked-orientation-lead"
              >
                {PORTAL_SUCHE_MARKED_ORIENTATION_LEAD}
              </h3>
              <div
                className="whitespace-pre-wrap text-sm text-slate-200 leading-relaxed"
                data-testid="portal-suche-marked-orientation"
              >
                {composeOrientation}
              </div>
              <h3 className="text-sm font-semibold text-white">{PORTAL_SUCHE_HEADING_RIS_BLOCKS}</h3>
              <div className="w-full space-y-4" data-testid="portal-suche-compose-blocks">
                {composeBlocks.map((block) => (
                  <SucheComposeChipBlock
                    key={block.key}
                    block={block}
                    fill={composeBlocks.length === 1}
                    arkoEntitled={arkoEntitled}
                  />
                ))}
              </div>
            </div>
          ) : null}
          {!showingCompose && !showExploreList && hit?.lexProof && !showLexOrExploreCards ? (
            <LexVerifiedQuoteBlock lexProof={hit.lexProof} heading="Wortlaut" />
          ) : null}
          <SucheResultFolds
            path={answerPath}
            text={proofText}
            lexProof={proofLex}
            judProof={proofJud}
          />
        </section>
      ) : null}
    </div>
  );
};

const PORTAL_SUCHE_PANE_ERROR =
  'Die Suche zeigt ihren Kopf. Die Treffer konnten gerade nicht gezeichnet werden.';

type SuchePaneBoundaryProps = {
  extraBody?: Record<string, unknown> | null;
  children: React.ReactNode;
};

type SuchePaneBoundaryState = { failed: boolean };

const SucheErrorShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const mark = portalSucheSurfaceClass(usePortalAppearance());
  return (
    <div
      className={`w-full min-w-0 max-w-none flex-1 overflow-y-auto space-y-3 min-h-[40vh] px-3 py-3 ${SUCHE_GROUND} ${mark}`}
      data-testid="portal-suche"
    >
      {children}
    </div>
  );
};

/** Ein Renderfehler lässt die Leiste stehen. Reload ist nicht der einzige Weg zurück. */
export class SuchePaneBoundary extends React.Component<SuchePaneBoundaryProps, SuchePaneBoundaryState> {
  state: SuchePaneBoundaryState = { failed: false };

  static getDerivedStateFromError(): SuchePaneBoundaryState {
    return { failed: true };
  }

  componentDidCatch(): void {
    clearPortalSuchePersist(portalSuchePersistSeatId(this.props.extraBody));
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <SucheErrorShell>
        <header className={`space-y-3 px-3.5 py-3 ${SUCHE_EDGE}`}>
          <h1 className="m-0 text-sm font-semibold text-white">{PORTAL_SUCHE_PAGE_TITLE}</h1>
          <p className="m-0 mt-0.5 text-[11px] text-slate-500">{PORTAL_SUCHE_PAGE_SUBTITLE}</p>
          <div
            className="flex items-center gap-1"
            role="tablist"
            aria-label={PORTAL_SUCHE_TABLIST_LABEL}
          >
            {portalSucheTabs(false).map((tab) => {
              const TabIcon = tab.id === 'jud' ? Scale : Search;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={tab.id === PORTAL_SUCHE_TABS[0]?.id}
                  aria-label={tab.label}
                  data-testid={`portal-suche-tab-${tab.id}`}
                  className={tab.id === PORTAL_SUCHE_TABS[0]?.id ? SUCHE_ICON_ON : SUCHE_ICON}
                >
                  <TabIcon className="h-3.5 w-3.5" aria-hidden />
                  <span className="sr-only">{tab.label}</span>
                </button>
              );
            })}
          </div>
          <form data-testid="portal-suche-form">
            <input
              type="search"
              readOnly
              value=""
              data-testid="portal-suche-input"
              aria-label={PORTAL_SUCHE_INPUT_LABEL_LEX}
              className="w-full rounded-md border border-violet-400/30 bg-[#07070a] px-3 py-2 text-sm text-slate-100"
            />
          </form>
        </header>
        <p role="alert" data-testid="portal-suche-pane-error" className="text-sm text-amber-100">
          {PORTAL_SUCHE_PANE_ERROR}
        </p>
        <button
          type="button"
          data-testid="portal-suche-pane-retry"
          onClick={() => this.setState({ failed: false })}
          className={SUCHE_BTN}
        >
          Erneut anzeigen
        </button>
      </SucheErrorShell>
    );
  }
}
